import React, { useState, ReactNode } from "react";
import {
  Radio,
  RadioProps,
  FormControlLabel,
  FormControlLabelProps,
  Box,
} from "@mui/material";

//
// ---- ToggleableRadio ----
//

interface ToggleableRadioProps
  extends Omit<FormControlLabelProps, "control" | "onChange"> {
  value: string;
  /** current selected value (injected by group) */
  selected?: string | undefined;
  /** called when this radio is clicked (injected by group) */
  onValueChange?: (value: string) => void;
  radioProps?: RadioProps;
}

export const ToggleableRadio: React.FC<ToggleableRadioProps> = ({
  value,
  selected,
  onValueChange,
  label,
  radioProps,
  ...rest
}) => {
  const checked = selected === value;

  const handleClick = () => {
    onValueChange?.(value);
  };

  return (
    <FormControlLabel
      {...rest}
      control={
        <Radio {...radioProps} checked={checked} onClick={handleClick} />
      }
      label={label}
    />
  );
};

//
// ---- ToggleableRadioGroup ----
//

interface ToggleableRadioGroupProps {
  children: ReactNode;
  value?: string | undefined;
  onChange?: (value: string | undefined) => void;
  row?: boolean;
}

export const ToggleableRadioGroup: React.FC<ToggleableRadioGroupProps> = ({
  children,
  value: controlledValue,
  row = false,
  onChange,
}) => {
  const [internalValue, setInternalValue] = useState<string | undefined>(
    controlledValue ?? undefined
  );

  const handleValueChange = (val: string) => {
    const newValue = internalValue === val ? undefined : val;
    setInternalValue(newValue);
    onChange?.(newValue);
  };

  // Inside ToggleableRadioGroup
  const enhancedChildren = React.Children.map(children, (child) => {
    if (!React.isValidElement<ToggleableRadioProps>(child)) return child;

    return React.cloneElement<ToggleableRadioProps>(child, {
      selected: internalValue,
      onValueChange: handleValueChange,
    });
  });

  return (
    <Box sx={{ display: "flex", flexDirection: `${row ? "row" : "column"}` }}>
      {enhancedChildren}
    </Box>
  );
};
